import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { Box, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useSelectedBookmarkSelector, useUserIdSelector } from "store";
import { IBookmark } from "store/types";

import { getCurrentPageContext } from "./bookmark-page";
import { ShareIcon } from "./ShareIcon";
import { useApplyBookmark } from "./use-apply-bookmark";
import { ViewRowMenu } from "./ViewRowMenu";

interface Props {
  bookmark: IBookmark;
  pageLabel: string;
  onApplied: () => void;
}

export const ViewRow: React.FC<Props> = ({ bookmark, pageLabel, onApplied }) => {
  const userId = useUserIdSelector();
  const selectedId = useSelectedBookmarkSelector();
  const { pathname } = useLocation();
  const applyBookmark = useApplyBookmark();

  const isOwn = bookmark.user_id === userId;
  const isActive = selectedId === bookmark.id;
  const canOverrideFromCurrentPage = getCurrentPageContext(pathname)?.type === bookmark.type;

  const handleClick = useCallback(() => {
    applyBookmark(bookmark);
    onApplied();
  }, [applyBookmark, bookmark, onApplied]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        padding: "8px 16px",
        position: "relative",
        cursor: "pointer",
        backgroundColor: isActive ? "#dbe8f5" : "transparent",
        "&:hover": { backgroundColor: isActive ? "#dbe8f5" : "#ecf1f8" }
      }}
      onClick={handleClick}
    >
      <Stack direction="row" alignItems="center" gap="8px" sx={{ flex: 1, minWidth: 0 }}>
        <MaterialTooltip title={bookmark.name} placement="top">
          <Typography
            sx={{
              fontSize: "14px",
              color: isActive ? "primary.main" : "#071f35",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0
            }}
          >
            {bookmark.name}
          </Typography>
        </MaterialTooltip>
        {bookmark.is_default && (
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 500,
              backgroundColor: "#e8e8e8",
              color: "#797979",
              padding: "1px 6px",
              borderRadius: "128px",
              flexShrink: 0
            }}
          >
            Default
          </Typography>
        )}
        {bookmark.account_default && (
          <MaterialTooltip title="This view is the default for everyone in your account." placement="top" arrow>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 500,
                backgroundColor: "#fdf3e7",
                color: "#c09560",
                padding: "1px 6px",
                borderRadius: "128px",
                flexShrink: 0
              }}
            >
              Account default
            </Typography>
          </MaterialTooltip>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap="2px" sx={{ flexShrink: 0 }}>
        {isOwn && <ShareIcon visibility={bookmark.visibility} sharedUsers={bookmark.shared_with} />}
        {isOwn && (
          <Box onClick={(e) => e.stopPropagation()}>
            <ViewRowMenu
              bookmark={bookmark}
              pageLabel={pageLabel}
              canOverrideFromCurrentPage={canOverrideFromCurrentPage}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
